import { QueryErrorResetBoundary } from "@tanstack/react-query";
import { Component, ErrorInfo } from "react";
import { GraphqlError, parseGraphqlError } from "../utils/graphql";

type Props = {
  fallback: (reset: () => void, error: ErrorState) => React.ReactNode;
  children: React.ReactNode;
};

export type ErrorState =
  | {
      type: "CLIENT_ERROR";
    }
  | {
      type: "SERVER_ERROR";
      error: GraphqlError;
    };

type State = { type: "NO_ERROR" } | ErrorState;

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { type: "NO_ERROR" };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const serverError = parseGraphqlError(error);

    serverError.match({
      Some: () => null,
      // We send error to backend only for client errors
      None: () => {
        const body = {
          app_version: __env.APP_VERSION,
          url: window.location.href,
          user_agent: window.navigator.userAgent,
          screen_width:
            window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth,
          screen_height:
            window.innerHeight ||
            document.documentElement.clientHeight ||
            document.body.clientHeight,
          message: error.message,
          stack: error.stack ?? "",
          component_stack: errorInfo.componentStack ?? "",
        };

        fetch("/front-end-error", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        });
      },
    });
  }

  static getDerivedStateFromError(error: Error): State {
    const serverError = parseGraphqlError(error);

    return serverError.match<State>({
      Some: (error) => ({ type: "SERVER_ERROR", error }),
      None: () => ({ type: "CLIENT_ERROR" }),
    });
  }

  resetError = (): void => {
    this.setState({ type: "NO_ERROR" });
  };

  render(): React.ReactNode {
    const { state } = this;
    return (
      <QueryErrorResetBoundary>
        {({ reset }) =>
          state.type === "NO_ERROR"
            ? this.props.children
            : state.type === "CLIENT_ERROR"
              ? this.props.fallback(this.resetError, state)
              : // state.type === "SERVER_ERROR"
                this.props.fallback(() => {
                  reset();
                  this.resetError();
                }, state)
        }
      </QueryErrorResetBoundary>
    );
  }
}
